import { ShieldCheck, ScanSearch, Fingerprint, KeyRound, Lock, ArrowUpRight, Activity, DollarSign, FileCheck2, BadgeCheck } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Parallax, DataCard, SectionBg } from "./Decor";
import { BOOKING_PATH, ASSETS } from "@/lib/site";
import { useLang } from "@/i18n/LanguageContext";
import { trackClick } from "@/lib/track";

const CHECK_ICONS = [ScanSearch, Fingerprint, KeyRound, Lock];
const BENEFIT_ICONS = [Activity, DollarSign, FileCheck2, BadgeCheck];

export const SecuritySection = () => {
  const { t } = useLang();
  const s = t.security;
  return (
    <section id="security" className="relative scroll-mt-24 py-24 lg:py-32 overflow-hidden">
      <SectionBg src={ASSETS.securityBg} />
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" />
      {/* security-tinted ambient glow + floating decor */}
      <div className="glow-orb animate-glow pointer-events-none" style={{ width: 460, height: 460, top: "8%", right: "-6%", background: "var(--green)", opacity: 0.22 }} />
      <Parallax className="hidden lg:block absolute z-0 right-[4%] top-[64px] pointer-events-none" from={20} to={-16}>
        <DataCard label="encryption" value="AES-256 ✓" tone="green" className="animate-float" />
      </Parallax>
      <Parallax className="hidden xl:block absolute z-0 right-[17%] top-[150px] pointer-events-none" from={-16} to={18}>
        <DataCard label="auth" value="MFA enforced" tone="green" className="animate-float-slow" />
      </Parallax>

      <div className="relative z-10 mx-auto max-w-7xl px-5">
        <SectionHeading eyebrow={s.eyebrow} title={s.title} intro={s.intro} testId="security-heading" />

        <div className="mt-14 grid lg:grid-cols-2 gap-6 items-start">
          {/* Secure System Audit checks */}
          <div data-testid="security-checks">
            <div className="flex items-center gap-2.5 mb-3">
              <ShieldCheck className="text-[var(--green)]" size={18} />
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--green)]">{s.auditEyebrow}</span>
            </div>
            <h3 className="font-display text-xl font-semibold text-white mb-5">{s.auditTitle}</h3>
            <div className="space-y-3">
              {s.checks.map((c, i) => {
                const Icon = CHECK_ICONS[i];
                return (
                  <div key={i} className="glass-card hover-lift rounded-2xl p-5 flex items-start gap-4" data-testid={`security-check-${i}`}>
                    <span className="grid place-items-center h-11 w-11 shrink-0 rounded-xl border border-[rgba(54,226,160,0.3)] bg-[rgba(54,226,160,0.08)] text-[var(--green)]">
                      <Icon size={20} />
                    </span>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--green)] border border-[rgba(54,226,160,0.35)] rounded px-1.5 py-0.5">{c.code}</span>
                        <h4 className="font-display font-semibold text-white">{c.title}</h4>
                      </div>
                      <p className="mt-1.5 text-sm text-muted leading-relaxed">{c.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* DevSecOps */}
          <div className="glass-card rounded-3xl p-7" data-testid="security-devsecops">
            <div className="flex items-center gap-2.5 mb-3">
              <Lock className="text-[var(--blue-bright)]" size={18} />
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--blue-bright)]">DevSecOps</span>
            </div>
            <h3 className="font-display text-2xl font-semibold text-white">{s.devsecopsTitle}</h3>
            <p className="mt-3 text-sm text-muted leading-relaxed">{s.devsecopsIntro}</p>

            <div className="divider-line my-6" />

            <div className="grid sm:grid-cols-2 gap-4">
              {s.benefits.map((b, i) => {
                const Icon = BENEFIT_ICONS[i];
                return (
                  <div key={i} className="panel rounded-2xl p-4" data-testid={`security-benefit-${i}`}>
                    <span className="grid place-items-center h-9 w-9 rounded-lg bg-[rgba(54,226,160,0.1)] text-[var(--green)] mb-3">
                      <Icon size={17} />
                    </span>
                    <h4 className="font-display font-semibold text-white text-sm">{b.title}</h4>
                    <p className="mt-1 text-xs text-muted leading-relaxed">{b.desc}</p>
                  </div>
                );
              })}
            </div>

            <a href={`${BOOKING_PATH}?src=security`} onClick={() => trackClick("book_time")} className="btn-primary w-full justify-center mt-7" data-testid="security-cta">
              {s.cta} <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
